import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Hash, Heart, Loader2 } from 'lucide-react';
import axios from 'axios';
import { avatarFor } from '@/lib/defaultAvatar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function GlobalSearch({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ users: [], content: [], tags: [] });
  const [loading, setLoading] = useState(false);

  // Focus the input the moment the modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults({ users: [], content: [], tags: [] });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setResults({ users: [], content: [], tags: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/search`, { params: { q, limit: 8 } });
        setResults(r.data || { users: [], content: [], tags: [] });
      } catch {
        setResults({ users: [], content: [], tags: [] });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [query, open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  const go = (path) => { onClose(); navigate(path); };

  const hasAny = results.users.length || results.content.length || results.tags.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-[#0A1514]/85 backdrop-blur-md flex items-start justify-center pt-16 sm:pt-24 px-4"
          onClick={onClose}
          data-testid="global-search-modal"
        >
          <motion.div
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-2xl bg-[#101F1D] border border-white/10 rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
              <Search className="w-5 h-5 text-[#1C4749] flex-shrink-0" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search creators, topics, or post ideas…"
                className="flex-1 bg-transparent text-white placeholder-white/40 text-base focus:outline-none"
              />
              {loading && <Loader2 className="w-4 h-4 text-[#1C4749] animate-spin" />}
              <button data-testid="search-close" onClick={onClose} className="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/5">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {!query.trim() && (
                <div className="px-6 py-10 text-center text-sm text-[#94A3B8]" data-testid="search-empty-state">
                  Start typing to search. Try a name, a topic like <span className="text-white">"mountain"</span>, or a creator handle.
                </div>
              )}

              {query.trim() && !loading && !hasAny && (
                <div className="px-6 py-10 text-center text-sm text-[#94A3B8]" data-testid="search-no-results">
                  No matches for <span className="text-white">"{query}"</span> yet.
                </div>
              )}

              {results.users.length > 0 && (
                <section className="px-3 py-3" data-testid="search-users">
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-[#1C4749]/80">Creators</p>
                  {results.users.map((u) => (
                    <button
                      key={u.id}
                      data-testid={`search-user-${u.username}`}
                      onClick={() => go(`/profile/${u.username}`)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors text-left"
                    >
                      <img
                        src={avatarFor(u)}
                        alt={u.username}
                        className="w-10 h-10 rounded-full object-cover border border-white/10"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">{u.display_name || u.username}</p>
                        <p className="text-xs text-[#94A3B8] truncate">@{u.username}{u.bio ? ` · ${u.bio}` : ''}</p>
                      </div>
                      <span className="text-xs text-[#94A3B8] flex-shrink-0">{u.followers_count} followers</span>
                    </button>
                  ))}
                </section>
              )}

              {results.tags.length > 0 && (
                <section className="px-3 py-3 border-t border-white/5" data-testid="search-tags">
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-[#1C4749]/80">Topics</p>
                  <div className="flex flex-wrap gap-2 px-3 py-1">
                    {results.tags.map((t) => (
                      <button
                        key={t.name}
                        data-testid={`search-tag-${t.name}`}
                        onClick={() => go(`/explore?tag=${encodeURIComponent(t.name)}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1C4749]/10 text-[#1C4749] border border-[#1C4749]/20 hover:bg-[#1C4749]/20 text-xs font-medium transition-colors"
                      >
                        <Hash className="w-3 h-3" /> {t.name} <span className="text-[#1C4749]/60">· {t.count}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {results.content.length > 0 && (
                <section className="px-3 py-3 border-t border-white/5" data-testid="search-content">
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-[#1C4749]/80">Posts</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 px-3 py-1">
                    {results.content.map((c) => (
                      <button
                        key={c.id}
                        data-testid={`search-post-${c.id}`}
                        onClick={() => go(`/explore`)}
                        className="group rounded-xl overflow-hidden bg-[#0A1514] border border-white/5 hover:border-white/15 transition-colors text-left"
                      >
                        <div className="aspect-square overflow-hidden">
                          <img src={c.url} alt={c.prompt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        </div>
                        <div className="p-2">
                          <p className="text-[11px] text-white line-clamp-2 leading-tight">{c.prompt || 'A creation'}</p>
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-[10px] text-[#94A3B8] truncate">@{c.user?.username || 'zenscroll'}</span>
                            <span className="inline-flex items-center gap-1 text-[10px] text-[#1C4749]">
                              <Heart className="w-3 h-3" /> {c.likes_count ?? 0}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <footer className="px-5 py-3 border-t border-white/5 text-[11px] text-[#94A3B8] flex items-center justify-between">
              <span>Press <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/80 text-[10px]">Esc</kbd> to close</span>
              <span>Press <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/80 text-[10px]">⌘</kbd> + <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/80 text-[10px]">K</kbd> to open</span>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
